import { useContext, useEffect, useState } from "react";
import type { History, Location } from "history";
import axios, { AxiosError } from "axios";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { endpoints } from "../endpoints";
import { Notifications } from "../components/Notifications/NotificationsContext";
import type { IPresetDataItem, IPresetFilter } from "../types/types";
import type { EntityType } from "../types/types.MDM";
import {
  convertToFiltersFormat,
  getStorageMethod,
  saveFilters,
} from "../util/filterStateManager";

/**
 * Shared saved-filter-preset state and actions for the product/entity list
 * pages (`EntityListPage.tsx`, `ProductsList.tsx`, `PortfolioModernView.tsx`),
 * which all drive the same `<PresetFilter>`/`<FiltersFlyBar>` pair against
 * the `v2_storefronts_id_user_filters(_type)` endpoints.
 */
export function useFilterPresets({
  storefrontId,
  entityType,
  canFetch = true,
  location,
  history,
  pathname,
  queryQ,
  queryOffset,
  queryPerPage,
}: {
  storefrontId: string;
  entityType?: EntityType;
  /** Gate for the underlying preset-list request — pass the same
   * condition the caller already uses to gate its other storefront-scoped
   * requests (e.g. `!roleIsGuest`, `!!storefront_id`). */
  canFetch?: boolean;
  /** Provide these (with the query params below) to auto-apply the
   * selected preset's filters to the URL/session storage on first load,
   * as long as no filters are already present — mirrors the product list
   * pages' "restore last-selected preset" behavior. Omit all of them for
   * surfaces that only need `selectedPreset` kept in sync without
   * auto-applying (e.g. the public portfolio view). */
  location?: Location;
  history?: History;
  pathname?: string;
  queryQ?: string | null;
  queryOffset?: number | null;
  queryPerPage?: number | null;
}) {
  const { t } = useTranslation();
  const { notifyError } = useContext(Notifications);

  const [selectedPreset, setSelectedPreset] = useState<IPresetDataItem>();
  const [appliedPresetIds, setAppliedPresetIds] = useState<Set<string>>(
    new Set()
  );

  const { data: presetFilterData, mutate: mutatePresetFilter } = useSWR<
    IPresetFilter,
    Error
  >(
    canFetch
      ? entityType
        ? endpoints.v2_storefronts_id_user_filters_type(
            storefrontId,
            entityType
          )
        : endpoints.v2_storefronts_id_user_filters(storefrontId)
      : null
  );

  const handleActivePresetId = async (
    presetId: string,
    isPresetSelected: boolean
  ) => {
    try {
      await axios.patch(
        entityType
          ? endpoints.v2_storefronts_id_user_filters_type_userFilterId(
              storefrontId,
              entityType,
              presetId
            )
          : endpoints.v2_storefronts_id_user_filters_userFilterId(
              storefrontId,
              presetId
            ),
        { selected: isPresetSelected }
      );

      await mutatePresetFilter();
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  /** Deselects the active preset (if any) and clears its `selected` flag
   * on the backend — the shared half of every list page's clear-filters
   * handler. Pass `preventPresetActivation` to skip this (e.g. when the
   * clear was itself triggered by selecting a different preset). */
  const clearSelectedPreset = async (preventPresetActivation?: boolean) => {
    if (selectedPreset && !preventPresetActivation) {
      setSelectedPreset(undefined);
      await handleActivePresetId(selectedPreset.id, false);
    }
  };

  useEffect(() => {
    if (!presetFilterData?.data) return;

    const selectedData = presetFilterData.data.find((item) => item.selected);
    setSelectedPreset(selectedData);

    if (!location || !history || !pathname) return;
    if (!selectedData?.filters || appliedPresetIds.has(selectedData.id)) {
      return;
    }
    if (getStorageMethod(location) !== null) return;

    setAppliedPresetIds((prev) => new Set(prev).add(selectedData.id));
    const filters = convertToFiltersFormat(selectedData.filters, t);
    saveFilters(filters, history, pathname, {
      q: queryQ || undefined,
      offset: queryOffset ? String(queryOffset) : undefined,
      perPage: queryPerPage ? String(queryPerPage) : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedPresetIds,
    history,
    location,
    pathname,
    presetFilterData?.data,
    queryOffset,
    queryPerPage,
    queryQ,
    t,
  ]);

  return {
    presetFilterData,
    mutatePresetFilter,
    selectedPreset,
    handleActivePresetId,
    clearSelectedPreset,
  };
}
